/**
 * <back-in-stock-form>
 *
 * Subscribes a shopper to Klaviyo back in stock notifications for the selected variant through
 * the client API. Klaviyo records this as "Subscribed to Back in Stock", which feeds the existing
 * back in stock flow.
 *
 * Docs: https://developers.klaviyo.com/en/docs/how_to_set_up_custom_back_in_stock
 */
(() => {
  if (customElements.get('back-in-stock-form')) return;

  const API_BASE = 'https://a.klaviyo.com/client';
  const API_REVISION = '2026-07-15';
  const LOG_PREFIX = '[back-in-stock]';

  class BackInStockForm extends HTMLElement {
    connectedCallback() {
      this.publicKey = this.dataset.publicKey;
      this.listId = this.dataset.listId;
      this.textTemplate = this.dataset.textTemplate || '';
      this.variants = this.parseVariants();

      this.form = this.querySelector('[data-bis-form]');
      this.text = this.querySelector('[data-bis-text]');
      this.emailInput = this.querySelector('[data-bis-email]');
      this.optIn = this.querySelector('[data-bis-optin]');
      this.submitButton = this.querySelector('[data-bis-submit]');
      this.error = this.querySelector('[data-bis-error]');
      this.success = this.querySelector('[data-bis-success]');

      // Variant pickers and the add-to-cart form live inside <product-info>.
      this.scope = this.closest('product-info') || this.closest('section') || document;

      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleScopeChange = this.handleScopeChange.bind(this);
      this.handleInput = () => this.clearError();

      this.form.addEventListener('submit', this.handleSubmit);
      this.emailInput.addEventListener('input', this.handleInput);
      this.scope.addEventListener('change', this.handleScopeChange);

      this.setVariant(this.dataset.variantId, { force: true });
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.handleSubmit);
      this.emailInput?.removeEventListener('input', this.handleInput);
      this.scope?.removeEventListener('change', this.handleScopeChange);
      clearTimeout(this.syncTimer);
    }

    parseVariants() {
      try {
        return JSON.parse(this.querySelector('[data-bis-variants]').textContent);
      } catch (error) {
        console.error(LOG_PREFIX, 'Could not read variant data', error);
        return [];
      }
    }

    /**
     * Public hook. If the theme's variant JS has a single place where the variant changes,
     * calling this from there is the most reliable option:
     *   document.querySelectorAll('back-in-stock-form').forEach((el) => el.setVariant(variant.id));
     */
    setVariant(id, { force = false } = {}) {
      const variant = this.variants.find((v) => String(v.id) === String(id));
      if (!variant) return;
      if (!force && this.variant?.id === variant.id) return;

      this.variant = variant;
      this.hidden = variant.available && !window.Shopify?.designMode;
      if (this.textTemplate) {
        this.text.textContent = this.textTemplate.split('[variant]').join(variant.name);
      }
      this.resetState();
    }

    handleScopeChange(event) {
      if (this.contains(event.target)) return;

      // The theme updates the hidden id input in response to the same change event, so read it
      // on the next frame. The delayed second read covers themes that update after an async render.
      requestAnimationFrame(() => this.syncFromPage());
      clearTimeout(this.syncTimer);
      this.syncTimer = setTimeout(() => this.syncFromPage(), 400);
    }

    syncFromPage() {
      const idInput = this.scope.querySelector('form[data-type="add-to-cart-form"] input[name="id"]');
      const urlId = new URLSearchParams(window.location.search).get('variant');
      this.setVariant(idInput?.value || urlId);
    }

    async handleSubmit(event) {
      event.preventDefault();
      if (this.busy || !this.variant) return;

      const email = this.emailInput.value.trim();
      if (!email || !this.emailInput.checkValidity()) {
        this.showError(this.dataset.errorEmail, { field: true });
        return;
      }

      this.setBusy(true);
      try {
        await this.post('back-in-stock-subscriptions', this.subscriptionPayload(email));

        if (this.optIn?.checked && this.listId) {
          // The newsletter signup is secondary, so it doesn't hold up the confirmation.
          this.post('subscriptions', this.newsletterPayload(email)).catch(() => {});
        }

        this.showSuccess();
      } catch (error) {
        if (error.pointer?.includes('email')) {
          this.showError(this.dataset.errorEmail, { field: true });
        } else {
          if (error.code === 'variant_not_found') {
            console.error(LOG_PREFIX, `Variant ${this.variant.id} is not in the Klaviyo catalog. Check the Shopify catalog sync.`);
          }
          this.showError(this.dataset.errorGeneric);
        }
      } finally {
        this.setBusy(false);
      }
    }

    async post(endpoint, payload) {
      const url = `${API_BASE}/${endpoint}/?company_id=${encodeURIComponent(this.publicKey)}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: API_REVISION },
        body: JSON.stringify(payload),
      });

      // Klaviyo answers 202 with an empty body on success.
      if (response.ok) return;

      const body = await response.json().catch(() => ({}));
      const apiError = body.errors?.[0] || {};
      const error = new Error(apiError.detail || `Klaviyo responded with ${response.status}`);
      error.status = response.status;
      error.code = apiError.code;
      error.pointer = apiError.source?.pointer;
      console.error(LOG_PREFIX, endpoint, error.message, body);
      throw error;
    }

    subscriptionPayload(email) {
      return {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: { data: { type: 'profile', attributes: { email } } },
            channels: ['EMAIL'],
          },
          relationships: {
            variant: {
              data: { type: 'catalog-variant', id: `$shopify:::$default:::${this.variant.id}` },
            },
          },
        },
      };
    }

    newsletterPayload(email) {
      return {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Back in stock form',
            profile: { data: { type: 'profile', attributes: { email } } },
          },
          relationships: {
            list: { data: { type: 'list', id: this.listId } },
          },
        },
      };
    }

    setBusy(busy) {
      // aria-disabled instead of disabled keeps keyboard focus on the button during the request.
      this.busy = busy;
      this.submitButton.setAttribute('aria-disabled', String(busy));
      this.submitButton.setAttribute('aria-busy', String(busy));
    }

    showError(message, { field = false } = {}) {
      this.error.hidden = false;
      this.error.textContent = message;
      if (field) {
        this.emailInput.setAttribute('aria-invalid', 'true');
        this.emailInput.focus();
      }
    }

    clearError() {
      this.error.hidden = true;
      this.error.textContent = '';
      this.emailInput.removeAttribute('aria-invalid');
    }

    showSuccess() {
      this.clearError();
      this.form.hidden = true;
      this.text.hidden = true;
      this.success.hidden = false;
      this.success.focus({ preventScroll: true });
    }

    resetState() {
      this.clearError();
      this.form.hidden = false;
      this.text.hidden = false;
      this.success.hidden = true;
    }
  }

  customElements.define('back-in-stock-form', BackInStockForm);
})();